import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/SEO/JsonLd';
import { allComics, categoryInfo, getComicsByCategory } from '@/mocks/comicMocks';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'update' | 'popular' | 'rating'>('update');

  const currentCategory = categoryInfo.find((c) => c.slug === slug) || categoryInfo[0];
  const comics = useMemo(() => getComicsByCategory(slug || 'love'), [slug]);

  const sortedComics = useMemo(() => {
    const list = [...comics];
    if (sortBy === 'update') {
      return list.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    if (sortBy === 'popular') {
      return list.sort((a, b) => b.views - a.views);
    }
    return list.sort((a, b) => b.rating - a.rating);
  }, [comics, sortBy]);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://knn08.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: 'http://knn08.com/category/all' },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 韩漫大全`,
    description: currentCategory.description,
    url: 'http://knn08.com/category/all',
    isPartOf: { '@type': 'WebSite', name: '韩漫大全', url: 'http://knn08.com/' },
    about: { '@type': 'Thing', name: currentCategory.name },
  };

  return (
    <>
      <JsonLd data={[breadcrumbSchema, webPageSchema]} />
      <Navbar />
      <main>
        {/* Hero Header */}
        <section className="pt-24 pb-10 bg-background-100">
          <div className="px-4 md:px-6">
            {/* Breadcrumb */}
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-foreground-600 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
                <li>
                  <Link to="/category/all" className="text-foreground-950 font-medium">{currentCategory.name}</Link>
                </li>
              </ol>
            </nav>

            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              {currentCategory.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl">
              {currentCategory.description} · 共收录 {currentCategory.count.toLocaleString()} 部作品
            </p>
          </div>
        </section>

        {/* Filter Tabs + Sort */}
        <section className="py-4 bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="px-4 md:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 w-full sm:w-auto">
              {categoryInfo.map((cat) => {
                const isActive = cat.slug === slug;
                return (
                  <Link
                    key={cat.slug}
                    to="/category/all"
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                );
              })}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {([
                { key: 'update', label: '最新更新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ] as const).map((option) => (
                <button
                  key={option.key}
                  onClick={() => setSortBy(option.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === option.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6">
            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {sortedComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        title={`${comic.title} - 韩漫大全免费阅读`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <div className="absolute top-2 left-2 flex gap-1">
                        <span className="px-2 py-0.5 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium">
                          {comic.status}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2">
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/50 text-white text-xs">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          {comic.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading font-bold text-foreground-950 text-sm truncate group-hover:text-primary-600 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                    <p className="text-xs text-primary-600 mt-1">{comic.latestChapter}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600 text-sm">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>

        {/* Recommended from other categories */}
        {sortedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                其他分类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {allComics
                  .filter((c) => c.categorySlug !== slug)
                  .sort(() => Math.random() - 0.5)
                  .slice(0, 5)
                  .map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3">
                        <img
                          src={comic.coverImage}
                          alt={comic.title}
                          title={`${comic.title} - 韩漫大全`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute top-2 right-2">
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/50 text-white text-xs">
                            <i className="ri-star-fill text-yellow-400 text-xs"></i>
                            {comic.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="font-heading font-bold text-foreground-950 text-sm truncate group-hover:text-primary-600 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.category}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}